import { type ReactNode, useEffect, useId, useState } from 'react';
import {
  fetchNotificationSettings,
  type NotificationSettings as Settings,
  saveNotificationSettings,
  unmuteGame,
} from '../../lib/notifications';
import { GameIcon } from '../games/GameArt';
import { Alert } from '../ui';

const cardClass = 'rounded-[18px] border border-line bg-card';

/** An on/off switch with its name and a line under it. */
export function Toggle({
  name,
  children,
  checked,
  busy,
  onChange,
}: {
  name: string;
  children: ReactNode;
  checked: boolean;
  busy: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex min-h-16 items-center gap-4 border-t border-divider px-5 py-3 sm:px-6">
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <b className="block font-semibold">{name}</b>
        <span className="block text-sm text-muted">{children}</span>
      </label>
      <input
        id={id}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        disabled={busy}
        onChange={(event) => onChange(event.target.checked)}
        className="relative h-[26px] w-11 shrink-0 cursor-pointer appearance-none rounded-full bg-[#d5d2e0] transition-colors before:absolute before:top-[3px] before:left-[3px] before:size-5 before:rounded-full before:bg-card before:shadow-[0_1px_2px_rgba(27,21,47,0.25)] before:transition-transform checked:bg-ink checked:before:translate-x-[18px] disabled:cursor-wait disabled:opacity-70"
      />
    </div>
  );
}

/**
 * Account → Notifications (M33): what the bell shows, and the opt-in emails for your turn,
 * with the games you stopped emails for.
 */
export function NotificationSettings({ username }: { username: string }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNotificationSettings()
      .then(setSettings)
      .catch(() => setError('Couldn’t load your settings. Reload the page to try again.'));
  }, []);

  async function save(change: () => Promise<Settings>) {
    setBusy(true);
    setError(null);
    try {
      setSettings(await change());
    } catch {
      setError('Couldn’t save that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }
  const set = (key: 'games' | 'replies' | 'achievements' | 'emailGames') => (on: boolean) => {
    if (settings) setSettings({ ...settings, [key]: on });
    return save(() => saveNotificationSettings({ [key]: on }));
  };

  if (!settings) {
    return error ? (
      <Alert>{error}</Alert>
    ) : (
      <p role="status" className="py-6 text-center text-sm text-muted">
        Loading…
      </p>
    );
  }

  return (
    <>
      {error && <Alert>{error}</Alert>}
      <section aria-labelledby="bell-heading" className={cardClass}>
        <div className="px-5 pt-5 pb-3 sm:px-6">
          <h2 id="bell-heading" className="text-lg leading-6 font-bold">
            The bell
          </h2>
          <p className="mt-1 text-sm text-muted">
            What shows under Notifications on MOGZO. Kept for 30 days.
          </p>
        </div>
        <Toggle name="Your games" checked={settings.games} busy={busy} onChange={set('games')}>
          Your turn, a match that starts or ends, and someone asking for a rematch
        </Toggle>
        <Toggle
          name="Replies and mentions"
          checked={settings.replies}
          busy={busy}
          onChange={set('replies')}
        >
          Replies to your posts and comments, and posts that mention @{username}, in communities
          you’re in
        </Toggle>
        <Toggle
          name="Achievements"
          checked={settings.achievements}
          busy={busy}
          onChange={set('achievements')}
        >
          Achievements you unlock in games
        </Toggle>
      </section>

      <section aria-labelledby="email-heading" className={cardClass}>
        <div className="px-5 pt-5 pb-3 sm:px-6">
          <h2 id="email-heading" className="text-lg leading-6 font-bold">
            Email
          </h2>
          <p className="mt-1 text-sm text-muted">
            Off until you turn it on. Sent to the email you sign in with.
          </p>
        </div>
        <Toggle
          name="Email me when it’s my turn, or someone wants a rematch"
          checked={settings.emailGames}
          busy={busy}
          onChange={set('emailGames')}
        >
          Only while you’re away from MOGZO, and at most one email an hour for each game. Every
          email has a link to stop them.
        </Toggle>
        {settings.muted.length > 0 && (
          <div className="border-t border-divider px-5 pt-3.5 pb-5 sm:px-6">
            <h3 className="mb-2 font-semibold">Games you stopped emails for</h3>
            <ul className="flex flex-col gap-1.5">
              {settings.muted.map((game) => (
                <li
                  key={game.projectId}
                  className="flex min-h-[52px] items-center gap-3 rounded-xl bg-field px-3"
                >
                  <GameIcon
                    game={{ name: game.name, iconUrl: null }}
                    className="size-8 rounded-[9px] text-[11px]"
                  />
                  <span className="min-w-0 flex-1 truncate font-semibold">{game.name}</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => save(() => unmuteGame(game.projectId))}
                    className="h-9 shrink-0 rounded-full border border-line bg-card px-3.5 text-[13px] font-semibold hover:border-muted disabled:opacity-60"
                  >
                    Email me again
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
      <p className="text-[13px] text-muted">
        Phone notifications come when MOGZO can be added to your home screen.
      </p>
    </>
  );
}
